import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Reusable delete confirmation dialog.
 *
 * Wrap the delete trigger (button/icon) as the child. Clicking the child opens a
 * confirmation dialog; confirming calls `onConfirm()`.
 *
 * Props:
 *  - itemLabel: short label of the record ("John Smith", "INV-000123", "Tractor 55HP")
 *  - entityLabel: what type of thing this is ("customer", "product", "sale"…)
 *  - onConfirm: async () => void — actual delete work (which typically also fires undo toast)
 *  - children: the trigger element (e.g., a red trash icon button)
 *  - testid: optional data-testid suffix for the Confirm button
 */
export default function ConfirmDelete({
  itemLabel,
  entityLabel = "item",
  onConfirm,
  children,
  testid,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testid ? `confirm-delete-dialog-${testid}` : "confirm-delete-dialog"}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">
            Delete this {entityLabel}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {itemLabel ? (
              <>
                <span className="font-semibold text-[#F8F9FA]">{itemLabel}</span> will be moved to
                Recently Deleted. You&apos;ll have 5 seconds to undo, or you can restore it later
                from the Trash.
              </>
            ) : (
              <>
                This {entityLabel} will be moved to Recently Deleted. You&apos;ll have 5 seconds
                to undo, or you can restore it later from the Trash.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={testid ? `confirm-delete-cancel-${testid}` : "confirm-delete-cancel"}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy}
            data-testid={testid ? `confirm-delete-confirm-${testid}` : "confirm-delete-confirm"}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
          >
            {busy ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
